import React, { useState } from 'react';
import { Wrench, Users, MessageSquare, ClipboardList, ChevronDown, Check } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth, crmSync } from '@/contexts/AuthContext';
import { ADDONS, money } from '@/data/constants';

const ICONS: Record<string, React.ElementType> = {
  crm: Wrench,
  crew: Users,
  chat: MessageSquare,
  logs: ClipboardList,
};

const AddonsPanel: React.FC = () => {
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState<string[]>([]);

  const request = async (code: string) => {
    setRequested((p) => [...p, code]);
    if (profile) {
      await crmSync({
        event: 'welcome',
        email: profile.email,
        name: profile.company_name || profile.email,
        phone: profile.phone,
        language: lang,
        userType: profile.user_type,
        details: { Addon: code },
      });
    }
  };

  return (
    <section className="mt-10 rounded-2xl border border-slate-200 bg-white">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
        <div>
          <h2 className="text-base font-extrabold text-slate-900">{t('dash.needTools')}</h2>
          <p className="text-sm text-slate-500">{t('dash.needToolsSub')}</p>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="grid gap-4 border-t border-slate-100 p-5 sm:grid-cols-2">
          {ADDONS.map((a) => {
            const Icon = ICONS[a.code];
            const isRequested = requested.includes(a.code);
            return (
              <div key={a.code} className="rounded-xl border border-slate-200 p-4">
                <Icon className="h-6 w-6 text-orange-500" />
                <h3 className="mt-3 font-bold text-slate-900">{t(`dash.addon.${a.code}`)}</h3>
                <p className="mt-1 text-sm text-slate-600">{t(`dash.addon.${a.code}Desc`)}</p>
                <p className="mt-3 text-sm font-extrabold text-slate-900">
                  {money(a.price)}
                  <span className="text-xs font-semibold text-slate-400">{t('pricing.perMonth')}</span>
                </p>
                <button
                  onClick={() => request(a.code)}
                  disabled={isRequested}
                  className={`mt-3 w-full rounded-lg px-3 py-2.5 text-sm font-bold ${
                    isRequested ? 'bg-emerald-50 text-emerald-700' : 'border border-slate-300 text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  {isRequested ? (
                    <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4" /> {t('dash.addonRequested')}</span>
                  ) : (
                    t('dash.addonRequest')
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AddonsPanel;
